import { toast } from "@/hooks/use-toast";
import {
  useUpdateTemplateFactory,
  UseUpdateTemplateType,
} from "@/hooks/notesTemplates/useUpdateTemplateFactory";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { CardContentTemplateGenerated } from "./CardContentTemplateGenerated";
import { useRegenerateTranscription } from "@/hooks/visitsTemplates/useRegenerateTranscription";
import type { FileData } from "@/services/files";
import { SplitSelectButton } from "@/components/ui/SplitSelectButton";
import { RefreshCcw } from "lucide-react";
import { useFileVersionStore } from "@/store/fileVersion.store";
import { FileResultRendererMD } from "@/components/ui/visits/file-result-renderer-md";
import { CardContentTemplate } from "@/components/ui/CardTemplate";
import { ParagraphSkeleton } from "@/components/ui/skeleton/ParagraphSkeleton";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { AUDIO_TRANSCRIPTION_NO_REALTIME_PROVIDERS } from "@/common/config";
import { TranscriptionNoRealtimeProviderName } from "@/hooks/transcriber/types";

interface Props {
  file: FileData;
  refresh: () => void;
  isRegenerateEnabled: boolean;
}

export const CardContentMainNote = ({
  file,
  refresh,
  isRegenerateEnabled,
}: Props): React.ReactNode => {
  const { updateTemplate, result, loading, error } = useUpdateTemplateFactory(
    UseUpdateTemplateType.VISIT
  );
  const {
    regenerate: regenerateTranscription,
    loading: loadingRegenerateTranscription,
  } = useRegenerateTranscription();

  const { fileVersion, loading: loadingFileVersion } = useFileVersionStore();

  const [providerSelected, setProviderSelected] = useState<string>(
    TranscriptionNoRealtimeProviderName.DEEPGRAM_NOVA_3_MEDICAL
  );

  const handleSave = async (value: string): Promise<void> => {
    await updateTemplate({ result: value, id: file.id });
  };

  const handleRegenerate = (): void => {
    void regenerateTranscription({
      fileId: file.id,
      audioTranscriptionProviderId: providerSelected,
    });
  };

  const hasFileOnBucket = (): boolean => {
    if (file.name && file.awsBucket) {
      return true;
    } else {
      return false;
    }
  };

  useEffect(() => {
    if (!loading) {
      if (error) {
        toast({
          variant: "error",
          title: t("visit.messages.updateFailed"),
        });
      }
      if (result) {
        toast({
          variant: "success",
          title: t("visit.messages.updateSuccessful"),
        });
        refresh();
      }
    }
  }, [error, loading, result, refresh]);

  if (fileVersion) {
    return (
      <CardContentTemplate isOpen file={file}>
        <div className="flex justify-between">
          <div className="border rounded px-2 py-1 text-sm">
            {t("visits.templateViewer.version.version")}: {fileVersion.version}
          </div>
        </div>
        <Separator className="my-4" />
        {loadingFileVersion ? (
          <ParagraphSkeleton lines={10} />
        ) : (
          <FileResultRendererMD result={fileVersion?.result || ""} />
        )}
      </CardContentTemplate>
    );
  }

  return (
    <CardContentTemplateGenerated
      file={file}
      loading={loadingRegenerateTranscription || loading}
      tools={
        hasFileOnBucket() && (
          <SplitSelectButton
            options={AUDIO_TRANSCRIPTION_NO_REALTIME_PROVIDERS}
            size="sm"
            value={providerSelected}
            variant={"default"}
            disabled={
              loadingRegenerateTranscription || loading || !isRegenerateEnabled
            }
            onClick={handleRegenerate}
            onValueChange={setProviderSelected}
          >
            <RefreshCcw
              className={cn(loadingRegenerateTranscription && "animate-spin")}
            />
            {t("visits.templateViewer.buttons.regenerate")}
          </SplitSelectButton>
        )
      }
      onSave={handleSave}
    />
  );
};
